import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button, ButtonGroup, Checkbox } from '@chakra-ui/react';

import { Modal, Textarea } from 'shared-components';
import { IMessage } from 'types';

import { getDisplayName } from '../../../../utilities/formatting';
import { ApplicationState } from '../../../../store';
import { UNI_ROLES, ENROLY_AGENT_ROLES, AGENT_ROLES, UserRole } from '../../../../store/auth/types';

export interface IProps {
  interviewName?: string;
  userRole: UserRole;
  onSendMessage: (message: IMessage) => void;
  onCancelRequest: (note: string) => void;
  onClose: () => void;
}

const CancelRecordedInterview = (props: IProps) => {
  const { interviewName, userRole, onSendMessage, onCancelRequest, onClose } = props;

  const { auth, config } = useSelector(({ auth, config }: ApplicationState) => ({ auth, config }), shallowEqual);

  const [message, setMessage] = useState('');
  const [cancelConfirmed, setCancelConfirmed] = useState(false);

  const cancelRequest = () => {
    if (!cancelConfirmed) {
      return;
    }

    onCancelRequest(message);

    const userName = auth.student_number! || 'Anonymous';
    const displayName = getDisplayName(auth, config);

    const msgReason = message ? `: ${message}` : '';

    const newMessage: IMessage = {
      isSendToStudent: false,
      messageType: 'note',
      author: userName,
      authorName: displayName || userName,
      messageFrom: 'uni',
      messageTo: 'uni',
      message: `Interview ${interviewName ? `<${interviewName}>` : ''} Cancelled${msgReason}`,
      createdAt: Date.now(),
      ...(UNI_ROLES.includes(userRole) ? { uniRead: true } : {}),
      ...(ENROLY_AGENT_ROLES.includes(userRole) ? { adminRead: true } : {}),
      ...(AGENT_ROLES.includes(userRole) ? { agentRead: true } : {}),
    };
    onSendMessage(newMessage);

    onClose();
  };

  return (
    <Modal
      id="cancel-recorded-interview"
      show={true}
      title={'Cancel recorded interview'}
      onClose={onClose}
      footer={
        <ButtonGroup size={'lg'} spacing={3}>
          <Button onClick={onClose}>Don't cancel</Button>

          <Button id="cancel-interview-button" onClick={cancelRequest} isDisabled={!cancelConfirmed} variant={'solid'}>
            Confirm cancellation
          </Button>
        </ButtonGroup>
      }
    >
      <Textarea
        id={'cancel-reason-message'}
        label="Cancellation reason"
        value={message}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) => setMessage(event.currentTarget.value)}
        placeholder={'Type your message here...'}
        mb={6}
      />

      <Checkbox
        id="confirm-cancel-action"
        isChecked={cancelConfirmed}
        onChange={() => setCancelConfirmed(!cancelConfirmed)}
        size={'lg'}
      >
        I understand that this interview will be cancelled
      </Checkbox>
    </Modal>
  );
};

export default CancelRecordedInterview;
